'use client'
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useUserContext } from "@/app/_module/store/contexts/userContext";

import { useResources, useResourceCategories, useAgencyCarriers } from "../hooks/use-hook";
import type { ResourceFilters } from "../types/types";

import { CategoryOverview } from "./CategoryOverview";
import { DocumentGrid } from "./DocumentGrid";
import { ResourceFilters as ResourceFiltersComponent } from "./ResourceFilters";

interface ResourceCenterProps {
  onCreateResource?: () => void;
  renderCreateButton?: () => React.ReactNode;
}

export const ResourceCenter = function({ onCreateResource, renderCreateButton }: ResourceCenterProps = {}) {
  const { user } = useUserContext();

  // Get agencyId from user context (nested in user.user or currentAgency)
  const userAgencyId = user?.user?.agencyId || user?.currentAgency?.agency?.id;

  const [filters, setFilters] = useState<ResourceFilters>({
    search: "",
    category: undefined,
    carrier: undefined,
    fileType: undefined,
    scope: undefined,
  });

  const userRole = (
    (user as any)?.currentAgency?.role ||
    (user as any)?.user?.agencyRole ||
    (user as any)?.user?.currentAgencyUser?.role ||
    ''
  ).toLowerCase();
  const isOwner = ['owner', 'admin'].includes(userRole);
  const userType = ((user as any)?.user?.userType || '').toLowerCase();
  const isAgencyOwner = isOwner || userType === 'agency_owner';

  const { data: resources = [], isLoading: resourcesLoading } = useResources(filters);
  const { data: categories = [], isLoading: categoriesLoading } = useResourceCategories();
  const { data: agencyCarriers = [], isLoading: carriersLoading } = useAgencyCarriers();

  // Fetch sub-agencies for the agency filter (owner only)
  const { data: subAgencies = [] } = useQuery<Array<{ id: string; name: string; depth: number }>>({
    queryKey: ['resource-sub-agencies', userAgencyId],
    queryFn: async () => {
      if (!userAgencyId) return [];
      try {
        const { getSupabaseSession } = await import("@/app/_module/services/auth");
        const { getApiUrl } = await import("@/app/_module/services/config");
        const AgencyTokenMgr = (await import("@/app/_module/services/agencyTokenManager")).default;
        const session = await getSupabaseSession();
        const agId = AgencyTokenMgr.getCurrentAgencyId();
        const res = await fetch(getApiUrl("agencies/sub-agencies/fetch"), {
          headers: { Authorization: `Bearer ${session?.access_token}`, ...(agId ? { 'x-agency-id': agId } : {}) },
        });
        if (!res.ok) return [];
        const json = await res.json();
        // Response shape: { statusCode, data: { items: [...] } }
        const raw = json?.data?.items || json?.data?.data || json?.data?.subAgencies || json?.data?.rows || json?.data || [];
        const list: any[] = Array.isArray(raw) ? raw : [];
        const currentName = (user as any)?.currentAgency?.agency?.name || 'My Agency';

        // Build tree with correct depths using parentAgencyId
        const result: Array<{ id: string; name: string; depth: number }> = [
          { id: userAgencyId, name: currentName, depth: 0 },
        ];
        const addChildren = (parentId: string, depth: number) => {
          const children = list.filter(a => a.parentAgencyId === parentId);
          for (const child of children) {
            result.push({ id: child.id, name: child.name || 'Sub-Agency', depth });
            addChildren(child.id, depth + 1);
          }
        };
        addChildren(userAgencyId, 1);
        // Add any orphans not matched (flat fallback)
        for (const a of list) {
          if (!result.some(r => r.id === a.id)) {
            result.push({ id: a.id, name: a.name || 'Sub-Agency', depth: 1 });
          }
        }
        return result;
      } catch { return []; }
    },
    enabled: !!userAgencyId,
    staleTime: 300000,
  });

  // Debug logging
  // Use agency carriers instead of extracting from resources
  const carriers = useMemo(() => {
    return agencyCarriers.map(carrier => ({
      id: carrier.name, // Use name as ID for filtering (matches resource.carrier string field)
      name: carrier.name,
    }));
  }, [agencyCarriers]);

  // Create an icon mapping based on category names
  const getCategoryIcon = (categoryName: string): string => {
    const iconMap: Record<string, string> = {
      'Applications': '📋',
      'Compliance': '✅',
      'Illustrations': '📊',
      'Marketing': '📢',
      'Training': '📚',
      'Underwriting': '🏥',
    };

    return iconMap[categoryName] || '📁'; // Default fallback icon
  };

  // Transform categories to include icons for DocumentGrid compatibility
  const categoriesWithIcons = useMemo(() => {
    return categories.map(category => ({
      ...category,
      icon: getCategoryIcon(category.name),
    }));
  }, [categories]);

  const updateFilters = (newFilters: Partial<ResourceFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleCategorySelect = (categoryId: string) => {
    // Toggle off if clicking the same category, otherwise set the new category
    if (filters.category === categoryId) {
      updateFilters({ category: undefined });
    } else {
      updateFilters({ category: categoryId === "all" ? undefined : categoryId });
    }
  };

  // Map file types - preserve the original type for display
  const mapFileType = (fileType: string): string => {
    // Simply return the file type as-is (uppercase for consistency)
    // The DocumentGrid handles all file types with proper icons and colors
    return fileType?.toUpperCase() || 'PDF';
  };

  // Convert resources to document format for DocumentGrid
  const documents = useMemo(() => {
    // Apply client-side agency filter
    const filtered = filters.agencyId
      ? resources.filter(r => (r as any).agencyId === filters.agencyId)
      : resources;

    return filtered.map(resource => ({
      ...resource,
      category: resource.categoryId,
      fileType: mapFileType(resource.fileType),
      downloadUrl: resource.fileUrl,
      tags: resource.tags || [],
      fileSize: resource.size,
      lastUpdated: new Date(resource.updatedAt),
      isPopular: resource.tags?.includes("Popular") || false,
      isLiked: resource.isLiked,
      likeCount: resource.likeCount,
    }));
  }, [resources, filters.agencyId]);

  if (categoriesLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading categories...</p>
        </div>
      </div>
    );
  }

  // Fallback categories if API returns empty data
  const fallbackCategories = [
    { id: 'applications', name: 'Applications', description: 'Insurance application forms', iconUrl: '', createdAt: '', updatedAt: '' },
    { id: 'compliance', name: 'Compliance', description: 'Regulatory and compliance documents', iconUrl: '', createdAt: '', updatedAt: '' },
    { id: 'illustrations', name: 'Illustrations', description: 'Policy illustrations and projections', iconUrl: '', createdAt: '', updatedAt: '' },
    { id: 'marketing', name: 'Marketing', description: 'Sales and marketing materials', iconUrl: '', createdAt: '', updatedAt: '' },
    { id: 'training', name: 'Training', description: 'Product training materials', iconUrl: '', createdAt: '', updatedAt: '' },
    { id: 'underwriting', name: 'Underwriting', description: 'Medical and financial underwriting forms', iconUrl: '', createdAt: '', updatedAt: '' },
  ];

  const displayCategories = categories.length > 0 ? categories : fallbackCategories;

  return (
    <div className="space-y-6">
      <CategoryOverview
        categories={displayCategories}
        onCategorySelect={handleCategorySelect}
        selectedCategoryId={filters.category}
      />

      <div className="bg-card rounded-lg shadow-sm border p-6">
        <ResourceFiltersComponent
          carriers={carriers}
          categories={displayCategories}
          filters={filters}
          onFiltersChange={updateFilters}
          showScopeFilter={!!userAgencyId}
          agencies={subAgencies}
          showAgencyFilter={true}
        />

        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">
              Documents ({documents.length})
            </h3>
            {renderCreateButton && (
              <div>
                {renderCreateButton()}
              </div>
            )}
          </div>
          
          {resourcesLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading resources...</p>
            </div>
          ) : (
            <DocumentGrid
              carriers={carriers}
              categories={categoriesWithIcons}
              documents={documents}
            />
          )}
        </div>
      </div>
    </div>
  );
}